import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

export default function Navbar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isHeaderScrolledOut, setIsHeaderScrolledOut] = useState(false);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);
  const location = useLocation();

  // Track when header has scrolled out of view to trigger floating CTA
  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 120;
      setIsHeaderScrolledOut(window.scrollY > headerHeight);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isServicesActive = [
    "/energiramme",
    "/energimaerkning",
    "/lca-beregning",
    "/termografi",
    "/fugtmaaling",
    "/trykproevning",
    "/termografi-komfortundersoegelse",
  ].includes(location.pathname);

  const servicesList = [
    {
      title: "Energimærkning",
      path: "/energimaerkning",
      desc: "Lovpligtig mærkning ved salg & leje",
      icon: "fa-certificate",
    },
    {
      title: "Energiramme",
      path: "/energiramme",
      desc: "Beregning til byggetilladelse & nybyg",
      icon: "fa-compass-drafting",
    },
    {
      title: "LCA-beregning",
      path: "/lca-beregning",
      desc: "Lovpligtig klimaberegning efter BR18",
      icon: "fa-leaf",
    },
    {
      title: "Trykprøvning / BlowerDoor",
      path: "/trykproevning",
      desc: "Test af bygningens tæthed & utætheder",
      icon: "fa-wind",
    },
    {
      title: "Termografi",
      path: "/termografi",
      desc: "Find kuldebroer & varmetab med IR-kamera",
      icon: "fa-temperature-arrow-down",
    },
    {
      title: "Fugtmåling",
      path: "/fugtmaaling",
      desc: "Find fugt i tag, væg, gulv & træ med Tramex",
      icon: "fa-droplet",
    },
    {
      title: "Termografi & komfortundersøgelse",
      path: "/termografi-komfortundersoegelse",
      desc: "Fejlsøgning ved træk, kulde & dårlig komfort",
      icon: "fa-house-chimney-crack",
    },
  ];

  const isOverlay = true;

  return (
    <>
      <header ref={headerRef} className="z-50 transition-all absolute top-0 left-0 right-0 bg-transparent">
      {/* Top Bar */}
      <div className="text-xs py-2 px-4 sm:px-6 bg-[#11463A]/85 backdrop-blur-xs text-stone-100 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center sm:justify-start">
            <a
              href="tel:+4542499595"
              className="flex items-center gap-2 hover:text-[#10B981] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#10B981]" aria-hidden="true"></i>
              <span className="font-semibold whitespace-nowrap">42 49 95 95</span>
            </a>
            <span className="hidden sm:inline text-stone-400">|</span>
            <a
              href="mailto:info@etdanmark.com"
              className="flex items-center gap-2 hover:text-[#10B981] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#10B981]" aria-hidden="true"></i>
              <span className="whitespace-nowrap">info@etdanmark.com</span>
            </a>
            <span className="hidden md:inline text-stone-400">|</span>
            <span className="hidden md:inline text-stone-300">
              <i className="fa-regular fa-clock mr-1 text-emerald-400" aria-hidden="true"></i>
              Svar inden for 24 timer
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 text-xs text-stone-200">
            {/* Certificeret Energikonsulent står for sig selv */}
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-950/70 border border-emerald-500/30 text-emerald-300 font-medium">
              <i className="fa-solid fa-certificate text-emerald-400 text-[10px]" aria-hidden="true"></i>
              Certificeret Energikonsulent
            </span>

            {/* Google anmeldelser stjerner - klikbart til anmeldelser */}
            <a
              href="/#anmeldelser"
              onClick={(e) => {
                const el = document.getElementById("anmeldelser");
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({ behavior: "smooth" });
                  window.history.pushState(null, "", "#anmeldelser");
                }
              }}
              title="Se vores 5-stjernede anmeldelser på Google & Trustpilot"
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-900/60 hover:bg-emerald-800/80 border border-emerald-500/30 hover:border-emerald-400/60 text-emerald-300 hover:text-emerald-100 font-medium transition-all duration-200 cursor-pointer group shadow-xs hover:shadow-sm"
            >
              <span className="flex text-amber-400 text-xs gap-0.5 group-hover:scale-105 transition-transform duration-200" aria-hidden="true">
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
              </span>
              <span className="group-hover:underline underline-offset-2 decoration-emerald-400/50">
                På Google & Trustpilot
              </span>
            </a>

            <span className="hidden lg:inline text-stone-300">
              Dækker hele Jylland & Fyn
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=260,quality=90,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/quick-logo-p-01-0_d4719c06.webp"
            alt="ET Danmark ApS"
            className="h-10 sm:h-12 w-auto object-contain transition-all duration-300 ease-out group-hover:scale-105 group-hover:brightness-110"
          />
        </Link>

        {/* Desktop Menu - ONLY >= 1200px (Rule: burger must activate under 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/20 text-white font-semibold shadow-xs"
                  : "text-stone-100 hover:text-white hover:bg-white/10"
              }`
            }
          >
            Hjem
          </NavLink>

          <NavLink
            to="/om-os"
            className={({ isActive }) =>
              `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/20 text-white font-semibold shadow-xs"
                  : "text-stone-100 hover:text-white hover:bg-white/10"
              }`
            }
          >
            Om os
          </NavLink>

          {/* Dropdown for Services */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              onMouseEnter={() => setIsServicesOpen(true)}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                isServicesActive || isServicesOpen
                  ? "bg-white/20 text-white font-semibold"
                  : "text-stone-100 hover:text-white hover:bg-white/10"
              }`}
              aria-expanded={isServicesOpen}
            >
              <span>Ydelser</span>
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                  isServicesOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {/* Dropdown Menu */}
            {isServicesOpen && (
              <div
                onMouseLeave={() => setIsServicesOpen(false)}
                className="absolute top-full left-0 w-80 mt-1 bg-white rounded-2xl shadow-xl border border-stone-200/80 p-2.5 grid gap-1.5 animate-in fade-in slide-in-from-top-2 duration-150"
              >
                <div className="px-3 py-2 bg-stone-50 rounded-xl mb-1 border border-stone-100">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Vores rådgivningsydelser
                  </p>
                </div>
                {servicesList.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    onClick={() => setIsServicesOpen(false)}
                    className={`flex items-start gap-3 p-2.5 rounded-xl transition-all ${
                      location.pathname === service.path
                        ? "bg-[#165B4C]/10 text-[#165B4C]"
                        : "hover:bg-stone-50 text-stone-800"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#165B4C]/10 text-[#165B4C] flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`fa-solid ${service.icon}`} aria-hidden="true"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{service.title}</span>
                        {service.badge && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300/60">
                            {service.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 line-clamp-1 mt-0.5">
                        {service.desc}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <NavLink
            to="/priser"
            className={({ isActive }) =>
              `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/20 text-white font-semibold shadow-xs"
                  : "text-stone-100 hover:text-white hover:bg-white/10"
              }`
            }
          >
            Priser
          </NavLink>

          <NavLink
            to="/galleri"
            className={({ isActive }) =>
              `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/20 text-white font-semibold shadow-xs"
                  : "text-stone-100 hover:text-white hover:bg-white/10"
              }`
            }
          >
            Galleri
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/20 text-white font-semibold shadow-xs"
                  : "text-stone-100 hover:text-white hover:bg-white/10"
              }`
            }
          >
            Kontakt
          </NavLink>
        </nav>

        {/* Right Action on Desktop */}
        <div className="hidden min-[1200px]:flex items-center gap-3">
          <Link
            to="/prisberegner"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all text-white bg-white/15 hover:bg-white/25 border border-white/25 backdrop-blur-xs shadow-sm"
          >
            <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
            <span>Prisberegner</span>
          </Link>
        </div>

        {/* Mobile / Tablet Burger Button - Active under 1200px */}
        <div className="flex min-[1200px]:hidden items-center gap-2">
          <Link
            to="/prisberegner"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-white/20 border border-white/30 text-white backdrop-blur-xs"
          >
            <i className="fa-solid fa-calculator" aria-hidden="true"></i>
            <span>Pris</span>
          </Link>

          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="p-2.5 rounded-xl focus:outline-none transition-colors text-white hover:bg-white/15"
            aria-label="Åbn menu"
          >
            <i
              className={`fa-solid ${
                isMobileOpen ? "fa-xmark" : "fa-bars"
              } text-xl`}
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay & Drawer (under 1200px) */}
      {isMobileOpen && (
        <div className="min-[1200px]:hidden border-t border-stone-200 bg-white px-4 py-6 shadow-2xl max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `p-3 rounded-xl text-base font-medium flex items-center justify-between ${
                  isActive
                    ? "bg-[#165B4C]/10 text-[#165B4C] font-semibold"
                    : "text-stone-800 hover:bg-stone-50"
                }`
              }
            >
              <span>Hjem</span>
              <i className="fa-solid fa-chevron-right text-xs text-stone-400" aria-hidden="true"></i>
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `p-3 rounded-xl text-base font-medium flex items-center justify-between ${
                  isActive
                    ? "bg-[#165B4C]/10 text-[#165B4C] font-semibold"
                    : "text-stone-800 hover:bg-stone-50"
                }`
              }
            >
              <span>Om os</span>
              <i className="fa-solid fa-chevron-right text-xs text-stone-400" aria-hidden="true"></i>
            </NavLink>

            {/* Mobile Accordion for Services */}
            <div className="rounded-xl border border-stone-200/80 overflow-hidden bg-stone-50/50">
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className="w-full p-3 text-left font-semibold text-stone-900 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <span>Ydelser</span>
                </div>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform ${
                    isMobileServicesOpen ? "rotate-180 text-[#165B4C]" : "text-stone-400"
                  }`}
                  aria-hidden="true"
                ></i>
              </button>

              {isMobileServicesOpen && (
                <div className="px-3 pb-3 pt-1 space-y-1 bg-white border-t border-stone-200/70">
                  {servicesList.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className={`flex items-center justify-between p-2.5 rounded-lg text-sm ${
                        location.pathname === service.path
                          ? "bg-[#165B4C]/10 text-[#165B4C] font-semibold"
                          : "text-stone-700 hover:bg-stone-50"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <i className={`fa-solid ${service.icon} text-stone-500 w-4`} aria-hidden="true"></i>
                        <span>{service.title}</span>
                      </div>
                      {service.badge && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                          {service.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/priser"
              className={({ isActive }) =>
                `p-3 rounded-xl text-base font-medium flex items-center justify-between ${
                  isActive
                    ? "bg-[#165B4C]/10 text-[#165B4C] font-semibold"
                    : "text-stone-800 hover:bg-stone-50"
                }`
              }
            >
              <span>Priser</span>
              <i className="fa-solid fa-chevron-right text-xs text-stone-400" aria-hidden="true"></i>
            </NavLink>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `p-3 rounded-xl text-base font-medium flex items-center justify-between ${
                  isActive
                    ? "bg-[#165B4C]/10 text-[#165B4C] font-semibold"
                    : "text-stone-800 hover:bg-stone-50"
                }`
              }
            >
              <span>Galleri</span>
              <i className="fa-solid fa-chevron-right text-xs text-stone-400" aria-hidden="true"></i>
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `p-3 rounded-xl text-base font-medium flex items-center justify-between ${
                  isActive
                    ? "bg-[#165B4C]/10 text-[#165B4C] font-semibold"
                    : "text-stone-800 hover:bg-stone-50"
                }`
              }
            >
              <span>Kontakt</span>
              <i className="fa-solid fa-chevron-right text-xs text-stone-400" aria-hidden="true"></i>
            </NavLink>
          </div>

          {/* Prisberegner CTA button at the bottom of mobile menu */}
          <div className="mt-6 pt-6 border-t border-stone-200">
            <Link
              to="/prisberegner"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 bg-[#165B4C] hover:bg-[#124b3e] text-white rounded-full font-semibold transition-colors shadow-sm"
            >
              <i className="fa-solid fa-calculator" aria-hidden="true"></i>
              <span>Prisberegner</span>
            </Link>
          </div>
        </div>
      )}
    </header>

    {/* Rolig flydende pop op i nederste højre hjørne der vises når headeren forsvinder */}
    <div
      className={`fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 transition-all duration-700 ease-out transform ${
        isHeaderScrolledOut
          ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
          : "opacity-0 translate-y-6 scale-95 pointer-events-none"
      }`}
    >
      <Link
        to="/prisberegner"
        aria-label="Indhent tilbud her - åbn prisberegner"
        className="group flex items-center gap-3 pl-3.5 pr-4 py-2.5 sm:py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white shadow-xl hover:shadow-2xl border border-emerald-400/40 backdrop-blur-md transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
      >
        <span className="relative flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 group-hover:bg-emerald-500/30 transition-colors">
          <i className="fa-solid fa-calculator text-sm" aria-hidden="true"></i>
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
          </span>
        </span>

        <div className="flex flex-col text-left">
          <span className="text-[11px] font-medium text-emerald-200/90 leading-none">
            Hurtig online beregning
          </span>
          <span className="text-sm font-bold text-white tracking-wide leading-tight flex items-center gap-1.5 mt-0.5">
            Indhent tilbud her
            <i
              className="fa-solid fa-arrow-right text-xs text-emerald-300 group-hover:translate-x-1 transition-transform duration-300"
              aria-hidden="true"
            ></i>
          </span>
        </div>
      </Link>
    </div>
  </>
  );
}
